import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';
import { getCurrentCorretor } from '../services/corretorAuthService';
import { uploadImage, uploadVideo, buildPropertySlug } from '../services/propertyService';
import FormPageV2 from '../admin/v2/FormPageV2';
import { MF_BASE } from '../basePath';

const TABLE = 'mf_imoveis';

const EMPTY_FORM = {
  titulo: '', descricao: '', tipo: 'apartamento',
  endereco: '', numero: '', cep: '',
  bairro: 'Alphaville', cidade: 'Barueri', condominio: '',
  area_m2: '', area_construida_m2: '', quartos: '', suites: '', banheiros: '', vagas: '', andar: '',
  mobiliado: false,
  preco: '', preco_condominio: '', iptu: '',
  preco_aluguel: '', preco_condominio_aluguel: '', iptu_aluguel: '',
  seo_title: '', seo_description: '', seo_keywords: '',
  imagens: [], amenidades: [], plantas: [],
  latitude: null, longitude: null,
  bianca_product_id: '',
  incluir_no_feed: false,
  status: 'rascunho', destaque: false,
  corretor_telefone: '', corretor_nome: '',
  comissao_percentual: '', comissao_obs: '',
  corretor_id: null,
};

export default function CorretorImovelFormPage() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);

  const [authState, setAuthState] = useState({ status: 'loading', corretor: null });
  const [form, setForm] = useState(EMPTY_FORM);
  const [loadingForm, setLoadingForm] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(null);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [galleryProgress, setGalleryProgress] = useState({ done: 0, total: 0, stage: '', fileName: '' });
  const [amenidadeInput, setAmenidadeInput] = useState('');

  // 1) Auth gate
  useEffect(() => {
    let cancelled = false;
    getCurrentCorretor().then(res => {
      if (cancelled) return;
      if (!res) { navigate(`${MF_BASE}/corretor/login`, { replace: true }); return; }
      setAuthState({ status: 'authed', corretor: res.corretor });
    });
    return () => { cancelled = true; };
  }, [navigate]);

  // 2) Load imovel (modo edicao). So permite editar imoveis do proprio corretor
  // e que ainda nao foram aprovados (apos aprovacao, alteracoes precisam passar
  // pela MF — voltariam a 'pendente').
  useEffect(() => {
    if (!isEdit || authState.status !== 'authed') return;
    let cancelled = false;
    supabase.from(TABLE).select('*').eq('id', editId).maybeSingle().then(({ data }) => {
      if (cancelled) return;
      if (!data || data.corretor_id !== authState.corretor.id) {
        alert('Imóvel não encontrado ou não pertence a você.');
        navigate(`${MF_BASE}/corretor`, { replace: true });
        return;
      }
      // Split "Rua X, 123" → rua + numero (mesmo padrao do AdminPage)
      const enderecoFull = data.endereco || '';
      const lastComma = enderecoFull.lastIndexOf(',');
      const splitNumero = lastComma >= 0 && /^\s*\d/.test(enderecoFull.slice(lastComma + 1));
      const ruaParte = splitNumero ? enderecoFull.slice(0, lastComma).trim() : enderecoFull;
      const numeroParte = splitNumero ? enderecoFull.slice(lastComma + 1).trim() : '';
      setForm({
        ...EMPTY_FORM,
        ...data,
        endereco: ruaParte,
        numero: numeroParte,
        area_m2: data.area_m2 ?? '',
        area_construida_m2: data.area_construida_m2 ?? '',
        quartos: data.quartos ?? '',
        suites: data.suites ?? '',
        banheiros: data.banheiros ?? '',
        vagas: data.vagas ?? '',
        andar: data.andar ?? '',
        preco: data.preco ?? '',
        preco_condominio: data.preco_condominio ?? '',
        iptu: data.iptu ?? '',
        preco_aluguel: data.preco_aluguel ?? '',
        preco_condominio_aluguel: data.preco_condominio_aluguel ?? '',
        iptu_aluguel: data.iptu_aluguel ?? '',
        comissao_percentual: data.comissao_percentual ?? '',
        comissao_obs: data.comissao_obs ?? '',
        imagens: data.imagens || [],
        amenidades: data.amenidades || [],
      });
      setLoadingForm(false);
    });
    return () => { cancelled = true; };
  }, [isEdit, editId, authState.status, authState.corretor, navigate]);

  // ===== Helpers de form (espelham AdminPage com versao simplificada) =====
  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));
  const toggleBool = (key) => () => setForm(f => ({ ...f, [key]: !f[key] }));

  const handleCepLookup = async () => {
    const cep = (form.cep || '').replace(/\D/g, '');
    if (cep.length !== 8) return;
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await res.json();
      if (data.erro) { alert('CEP não encontrado'); return; }
      setForm(f => ({
        ...f,
        endereco: data.logradouro || f.endereco,
        bairro: data.bairro || f.bairro,
        cidade: data.localidade || f.cidade,
      }));
    } catch (err) {
      console.warn('Erro ViaCEP:', err);
    }
  };

  const formatCep = (v) => {
    const digits = (v || '').replace(/\D/g, '').slice(0, 8);
    if (digits.length > 5) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
    return digits;
  };

  const setMoney = (key) => (e) => {
    const digits = (e.target.value || '').replace(/\D/g, '');
    if (!digits) return setForm(f => ({ ...f, [key]: '' }));
    setForm(f => ({ ...f, [key]: Number(digits) / 100 }));
  };
  const fmtMoney = (v) => {
    if (v === '' || v == null) return '';
    const n = Number(v);
    if (!Number.isFinite(n)) return '';
    return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const addAmenidade = () => {
    if (!amenidadeInput.trim()) return;
    setForm(f => ({ ...f, amenidades: [...f.amenidades, amenidadeInput.trim()] }));
    setAmenidadeInput('');
  };
  const removeAmenidade = (i) => {
    setForm(f => ({ ...f, amenidades: f.amenidades.filter((_, idx) => idx !== i) }));
  };

  const removeImage = (i) => {
    setForm(f => ({ ...f, imagens: f.imagens.filter((_, idx) => idx !== i) }));
  };
  const setAsCover = (i) => {
    if (i === 0) return;
    setForm(f => {
      const next = [...f.imagens];
      const [picked] = next.splice(i, 1);
      next.unshift(picked);
      return { ...f, imagens: next };
    });
    setViewerIndex(0);
  };
  const reorderImages = (fromIdx, toIdx) => {
    if (fromIdx === toIdx || fromIdx == null || toIdx == null) return;
    setForm(f => {
      const list = f.imagens || [];
      if (fromIdx < 0 || fromIdx >= list.length || toIdx < 0 || toIdx >= list.length) return f;
      const next = [...list];
      const [picked] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, picked);
      return { ...f, imagens: next };
    });
  };

  const handleGalleryFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setGalleryUploading(true);
    setGalleryProgress({ done: 0, total: files.length, stage: '', fileName: '' });
    const folder = (form.titulo || 'imovel').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'imovel';
    for (const file of files) {
      const isVid = file.type.startsWith('video/');
      const setStage = (stage) => setGalleryProgress(p => ({ ...p, stage, fileName: file.name }));
      try {
        const url = isVid ? await uploadVideo(file, folder, setStage) : await uploadImage(file, folder);
        if (url) setForm(f => ({ ...f, imagens: [...f.imagens, url] }));
      } catch (err) {
        console.error('Falha no upload de', file.name, err);
        window.alert(`Erro ao processar "${file.name}": ${err.message || 'tente outro arquivo.'}`);
      }
      setGalleryProgress(p => ({ ...p, done: p.done + 1, stage: '', fileName: '' }));
    }
    setGalleryUploading(false);
    setGalleryProgress({ done: 0, total: 0, stage: '', fileName: '' });
  };

  // ===== SAVE =====
  // Corretor sempre salva como pendente. corretor_id e dados de contato sao
  // forcados a partir do cadastro dele — nao confia em campo livre.
  const handleSave = async () => {
    if (!form.titulo) { alert('Informe o título do imóvel.'); return; }
    setSaving(true);
    const c = authState.corretor;
    const enderecoCompleto = form.numero ? `${form.endereco}, ${form.numero}` : form.endereco;
    const payload = {
      ...form,
      endereco: enderecoCompleto,
      area_m2: form.area_m2 ? Number(form.area_m2) : null,
      area_construida_m2: form.area_construida_m2 ? Number(form.area_construida_m2) : null,
      quartos: form.quartos ? Number(form.quartos) : null,
      suites: form.suites ? Number(form.suites) : null,
      banheiros: form.banheiros ? Number(form.banheiros) : null,
      vagas: form.vagas ? Number(form.vagas) : null,
      andar: form.andar ? Number(form.andar) : null,
      preco: form.preco ? Number(form.preco) : null,
      preco_condominio: form.preco_condominio ? Number(form.preco_condominio) : null,
      iptu: form.iptu ? Number(form.iptu) : null,
      preco_aluguel: form.preco_aluguel ? Number(form.preco_aluguel) : null,
      preco_condominio_aluguel: form.preco_condominio_aluguel ? Number(form.preco_condominio_aluguel) : null,
      iptu_aluguel: form.iptu_aluguel ? Number(form.iptu_aluguel) : null,
      comissao_percentual: form.comissao_percentual ? Number(form.comissao_percentual) : null,
      // Forcados do corretor logado:
      corretor_id: c.id,
      corretor_nome: c.nome || form.corretor_nome,
      corretor_telefone: (c.telefone || '').replace(/\D/g, '') || form.corretor_telefone,
      // Edicao de imovel ja aprovado: mantem status atual, mas volta pra pendente
      // pra MF reaprovar. Site publico filtra status_aprovacao='aprovado', entao
      // some automaticamente do listing ate ser reaprovado.
      status: isEdit ? (form.status || 'rascunho') : 'rascunho',
      status_aprovacao: 'pendente',
      motivo_rejeicao: null,
      updated_at: new Date().toISOString(),
    };
    delete payload.id;
    delete payload.created_at;
    delete payload.numero;
    delete payload.cep;

    try {
      if (isEdit) {
        const { error } = await supabase.from(TABLE).update(payload).eq('id', editId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from(TABLE).insert(payload);
        if (error) throw error;
      }
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
      // Volta pro painel apos pequena pausa
      setTimeout(() => navigate(`${MF_BASE}/corretor`), 600);
    } catch (err) {
      alert('Erro ao salvar: ' + (err.message || err));
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    const { error } = await supabase.from(TABLE).delete().eq('id', id).eq('corretor_id', authState.corretor.id);
    if (error) { alert('Erro ao excluir: ' + error.message); return; }
  };

  const previewHref = isEdit && form.status_aprovacao === 'aprovado'
    ? `/imoveis/${buildPropertySlug({ id: editId, titulo: form.titulo })}`
    : null;

  if (authState.status === 'loading' || loadingForm) {
    return <div style={{ minHeight: '100vh', background: '#0F1828' }} />;
  }

  return (
    <FormPageV2
      formMode="corretor"
      form={form}
      setForm={setForm}
      set={set}
      editId={isEdit ? editId : null}
      saving={saving}
      savedFlash={savedFlash}
      onSave={handleSave}
      onDelete={handleDelete}
      onBack={() => navigate(`${MF_BASE}/corretor`)}
      toggleBool={toggleBool}
      viewerIndex={viewerIndex}
      setViewerIndex={setViewerIndex}
      handleGalleryFiles={handleGalleryFiles}
      removeImage={removeImage}
      setAsCover={setAsCover}
      reorderImages={reorderImages}
      galleryUploading={galleryUploading}
      galleryProgress={galleryProgress}
      amenidadeInput={amenidadeInput}
      setAmenidadeInput={setAmenidadeInput}
      addAmenidade={addAmenidade}
      removeAmenidade={removeAmenidade}
      handleCepLookup={handleCepLookup}
      formatCep={formatCep}
      fmtMoney={fmtMoney}
      setMoney={setMoney}
      previewHref={previewHref}
      SeoSection={null}
    />
  );
}
